/**
 * The visual-layer debug flag — shared by the overlay that draws the boxes
 * (`visual-debug-layer.tsx`) and the toggle in the version-info debug panel.
 *
 * Deliberately a client-only, per-browser flag rather than a persisted view setting: this is a
 * developer diagnostic, not a document preference, so it must never round-trip to the API alongside
 * real view settings like the bleed bar or guides.
 *
 * Three equivalent ways to set it, all landing on the same state:
 *   - the toggle in the version badge's expanded panel
 *   - `__larryVisualDebug(true)` from the console
 *   - `?visualDebug=1` in the URL, which wins over stored state and survives reloads
 */

export const VISUAL_DEBUG_STORAGE_KEY = 'larry:visual-debug'
/**
 * Same-tab change notification. The DOM `storage` event only fires in *other* tabs, never the one
 * that wrote the value, so listeners need this to see a local change without a reload.
 */
export const VISUAL_DEBUG_CHANGE_EVENT = 'larry:visual-debug-changed'
/** Backstop for a value written directly to localStorage, which emits no event in this tab. */
export const VISUAL_DEBUG_POLL_MS = 1000

/** URL param wins over stored state so a link can force the overlay on. */
export function readVisualDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false

  try {
    if (new URLSearchParams(window.location.search).get('visualDebug') === '1') return true

    return window.localStorage.getItem(VISUAL_DEBUG_STORAGE_KEY) === '1'
  } catch {
    // Private browsing / storage disabled — the overlay simply stays off.
    return false
  }
}

export function setVisualDebugEnabled(next: boolean): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(VISUAL_DEBUG_STORAGE_KEY, next ? '1' : '0')
  } catch {
    // Ignore — the dispatch below still updates every listener in this tab.
  }

  window.dispatchEvent(new CustomEvent(VISUAL_DEBUG_CHANGE_EVENT))
}

type VisualDebugWindow = Window & { __larryVisualDebug?: (on?: boolean) => boolean }

/**
 * Exposes `window.__larryVisualDebug(on?)`. Idempotent, so every consumer can call it without
 * coordinating. Returns the resulting state; omitting the argument flips the current one.
 */
export function installVisualDebugConsoleToggle(): void {
  if (typeof window === 'undefined') return

  const w = window as VisualDebugWindow

  if (w.__larryVisualDebug) return

  w.__larryVisualDebug = (on?: boolean) => {
    const next = on ?? !readVisualDebugEnabled()

    setVisualDebugEnabled(next)
    return next
  }
}
